/* src/modules/hr/ExitManagement.js */
import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, serverTimestamp, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
    CheckCircleIcon, ExclamationIcon, BriefcaseIcon, CreditCardIcon, 
    ClipboardListIcon, ComputerDesktopIcon, EyeIcon, ChevronDownIcon, ChevronUpIcon,
    DownloadIcon
} from '../../components/icons/Icons';

export default function ExitManagement({ appId, userProfile }) {
    const [offboardingUsers, setOffboardingUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    
    const [viewMode, setViewMode] = useState('Active'); // 'Active' or 'Past'

    // Role-based Access Flags
    const isHRAdmin = userProfile.role === 'Admin' || userProfile.department === 'HR';
    const isIT = userProfile.role === 'Admin' || userProfile.department === 'IT';
    const isFinance = userProfile.role === 'Admin' || (userProfile.department === 'Finance' && userProfile.role === 'Approver');

    useEffect(() => {
        const statusToQuery = viewMode === 'Active' ? 'Approved' : 'Deactivated';
        const q = query(collection(db, `/artifacts/${appId}/users`), where("status", "==", statusToQuery));
        
        const unsub = onSnapshot(q, (snapshot) => {
            const allUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            let pendingExits = allUsers.filter(u => u.lastWorkingDate != null);

            pendingExits = pendingExits.map(u => ({
                ...u,
                exitClearance: u.exitClearance || {
                    manager: { status: 'Pending', signedOffBy: null, signedOffAt: null, notes: '' },
                    it: { status: 'Pending', signedOffBy: null, signedOffAt: null, notes: '' },
                    hr: { status: 'Pending', signedOffBy: null, signedOffAt: null, notes: '' },
                    finance: { status: 'Pending', signedOffBy: null, signedOffAt: null, notes: '' },
                    overallStatus: 'Pending'
                }
            }));

            if (!isHRAdmin && !isIT && !isFinance) {
                pendingExits = pendingExits.filter(u => u.managerId === userProfile.id);
            }

            setOffboardingUsers(pendingExits);
            
            if (selectedUser) {
                const updatedSelected = pendingExits.find(u => u.id === selectedUser.id);
                setSelectedUser(updatedSelected || null);
            }
            
            setIsLoading(false);
        });

        return () => unsub();
    }, [appId, userProfile.id, isHRAdmin, isIT, isFinance, selectedUser, viewMode]);

    if (isLoading) return <LoadingSpinner />;

    if (selectedUser) {
        return (
            <OffboardingChecklist 
                key={selectedUser.id}
                appId={appId} 
                user={selectedUser} 
                userProfile={userProfile}
                isHRAdmin={isHRAdmin}
                isIT={isIT}
                isFinance={isFinance}
                onBack={() => setSelectedUser(null)} 
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-6 rounded-lg shadow-sm border border-gray-200 gap-4">
                <div>
                    <h2 className="text-xl font-bold text-[#213368]">Exit Management</h2>
                    <p className="text-sm text-gray-500">Track and sign off on employee offboarding clearances.</p>
                </div>
                
                <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200">
                    <button 
                        onClick={() => setViewMode('Active')}
                        className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${viewMode === 'Active' ? 'bg-white text-[#213368] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Current Exits
                    </button>
                    <button 
                        onClick={() => setViewMode('Past')}
                        className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${viewMode === 'Past' ? 'bg-white text-[#213368] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Past Exits
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                {/* --- ADDED THIS WRAPPER --- */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Employee</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Last Working Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Overall Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Pending Depts</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {offboardingUsers.map(user => {
                                const c = user.exitClearance;
                                const pendingDepts = [];
                                if (c.manager?.status !== 'Cleared') pendingDepts.push('Manager');
                                if (c.it?.status !== 'Cleared') pendingDepts.push('IT');
                                if (c.hr?.status !== 'Cleared') pendingDepts.push('HR');
                                if (c.finance?.status !== 'Cleared') pendingDepts.push('Finance');

                                return (
                                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-[#213368]">{user.name}</div>
                                            <div className="text-xs text-gray-500">{user.department}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-bold text-[#D95448]">
                                            {new Date(user.lastWorkingDate.toDate()).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-bold rounded-full ${c.overallStatus === 'Cleared' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {c.overallStatus}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {pendingDepts.length > 0 ? pendingDepts.join(', ') : 'None'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <button 
                                                onClick={() => setSelectedUser(user)}
                                                title="View Clearances"
                                                className="text-[#20BEC6] hover:text-[#189b9f] transition p-2"
                                            >
                                                <EyeIcon className="w-5 h-5" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {offboardingUsers.length === 0 && (
                                <tr><td colSpan="5" className="text-center py-8 text-gray-500">No {viewMode.toLowerCase()} offboarding records found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* --- CLOSING WRAPPER --- */}
            </div>
        </div>
    );
}

// =========================================================
// DETAILS & ACCORDION COMPONENT
// =========================================================
function OffboardingChecklist({ appId, user, userProfile, isHRAdmin, isIT, isFinance, onBack }) {
    const clearance = user.exitClearance;
    // --- UPDATED: Admin users can sign off for the manager ---
    const isManager = user.managerId === userProfile.id || userProfile.role === 'Admin';

    // --- NEW: Initialize expanded sections based ONLY on Cleared status ---
    const [expandedSections, setExpandedSections] = useState(() => {
        const clearedDepts = [];
        if (clearance.manager?.status === 'Cleared') clearedDepts.push('manager');
        if (clearance.it?.status === 'Cleared') clearedDepts.push('it');
        if (clearance.finance?.status === 'Cleared') clearedDepts.push('finance');
        if (clearance.hr?.status === 'Cleared') clearedDepts.push('hr');
        return clearedDepts;
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => 
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // --- NEW: Calculate HR Dependencies ---
    const hrBlockingDepts = [];
    if (clearance.manager?.status !== 'Cleared') hrBlockingDepts.push('Reporting Manager');
    if (clearance.it?.status !== 'Cleared') hrBlockingDepts.push('IT');
    if (clearance.finance?.status !== 'Cleared') hrBlockingDepts.push('Accounts & Finance');

    const handleSignOff = async (deptKey, checklistData) => {
        if (!window.confirm(`Confirm and sign off on ${deptKey.toUpperCase()} clearance for ${user.name}?`)) return;

        const updatedClearance = { ...clearance };
        updatedClearance[deptKey] = {
            ...updatedClearance[deptKey],
            ...checklistData,
            status: 'Cleared',
            signedOffBy: userProfile.name,
            signedOffAt: new Date().toISOString()
        };

        const allCleared = ['manager', 'it', 'hr', 'finance'].every(d => updatedClearance[d].status === 'Cleared');
        if (allCleared) updatedClearance.overallStatus = 'Cleared';

        try {
            await updateDoc(doc(db, `artifacts/${appId}/users`, user.id), {
                exitClearance: updatedClearance,
                updatedAt: serverTimestamp()
            });
            // Ensure the newly cleared section stays expanded so they can see their sign-off
            setExpandedSections(prev => prev.includes(deptKey) ? prev : [...prev, deptKey]); 
        } catch (error) {
            console.error("Sign off error:", error);
            alert("Failed to sign off. Check permissions.");
        }
    };

    const handleUndoSignOff = async (deptKey) => {
        if (!window.confirm(`Are you sure you want to revoke the ${deptKey.toUpperCase()} clearance for ${user.name}?`)) return;

        const updatedClearance = { ...clearance };
        updatedClearance[deptKey] = {
            ...updatedClearance[deptKey],
            status: 'Pending',
            signedOffBy: null,
            signedOffAt: null
        };
        
        updatedClearance.overallStatus = 'Pending';

        try {
            await updateDoc(doc(db, `artifacts/${appId}/users`, user.id), {
                exitClearance: updatedClearance,
                updatedAt: serverTimestamp()
            });
            // Keep it expanded so they can adjust notes and sign off again
            setExpandedSections(prev => prev.includes(deptKey) ? prev : [...prev, deptKey]); 
        } catch (error) {
            console.error("Undo error:", error);
            alert("Failed to undo sign off. Check permissions.");
        }
    };

    const handleDownloadPDF = () => {
        window.print();
    };

    return (
        <div>
            {/* --- INTERACTIVE WEB UI (Hidden during print) --- */}
            <div className="space-y-6 max-w-4xl mx-auto print:hidden">
                <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#213368] font-medium transition">← Back to Exit List</button>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-[#213368]">{user.name} - Clearance Checklist</h2>
                        <p className="text-sm text-gray-500 mt-1">Last Working Date: <strong className="text-[#D95448]">{new Date(user.lastWorkingDate.toDate()).toLocaleDateString()}</strong></p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <span className={`px-4 py-2 text-sm font-bold rounded-full ${clearance.overallStatus === 'Cleared' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            Overall: {clearance.overallStatus}
                        </span>
                        
                        <button 
                            onClick={handleDownloadPDF}
                            className="flex items-center px-4 py-2 bg-gray-100 text-[#213368] border border-gray-300 font-bold rounded-lg hover:bg-gray-200 transition shadow-sm text-sm"
                            title="Download Form"
                        >
                            <DownloadIcon className="w-5 h-5 mr-2" /> Download Form
                        </button>
                    </div>
                </div>

                <div className="space-y-4">
                    <ClearanceAccordion 
                        title="Reporting Manager" 
                        icon={<BriefcaseIcon className="w-6 h-6 text-[#213368]" />}
                        data={clearance.manager}
                        canEdit={isManager}
                        deptKey="manager"
                        isExpanded={expandedSections.includes('manager')}
                        onToggle={() => toggleSection('manager')}
                        onSignOff={handleSignOff}
                        onUndo={handleUndoSignOff}
                        checklistItems={[
                            { key: 'ipHandover', label: 'Handover & transition of all work-related data and IP completed.' }
                        ]}
                    />

                    <ITClearanceAccordion 
                        appId={appId}
                        userId={user.id}
                        data={clearance.it}
                        canEdit={isIT}
                        isExpanded={expandedSections.includes('it')}
                        onToggle={() => toggleSection('it')}
                        onSignOff={handleSignOff}
                        onUndo={handleUndoSignOff}
                    />

                    <ClearanceAccordion 
                        title="Accounts & Finance" 
                        icon={<CreditCardIcon className="w-6 h-6 text-[#213368]" />}
                        data={clearance.finance}
                        canEdit={isFinance}
                        deptKey="finance"
                        isExpanded={expandedSections.includes('finance')}
                        onToggle={() => toggleSection('finance')}
                        onSignOff={handleSignOff}
                        onUndo={handleUndoSignOff}
                        checklistItems={[
                            { key: 'fnfCleared', label: 'Full and Final (F&F) Settlement cleared. No outstanding dues or loans.' }
                        ]}
                    />

                    {/* --- UPDATED: HR Card now receives blockingDepts --- */}
                    <ClearanceAccordion 
                        title="HR" 
                        icon={<ClipboardListIcon className="w-6 h-6 text-[#213368]" />}
                        data={clearance.hr}
                        canEdit={isHRAdmin}
                        deptKey="hr"
                        isExpanded={expandedSections.includes('hr')}
                        onToggle={() => toggleSection('hr')}
                        onSignOff={handleSignOff}
                        onUndo={handleUndoSignOff}
                        blockingDepts={hrBlockingDepts}
                        checklistItems={[
                            { key: 'idCardDeactivated', label: 'ID card deactivated.' },
                            { key: 'accessRevoked', label: 'Biometric / other access revoked.' },
                            { key: 'deskCleared', label: 'Desk and drawers checked / cleared.' },
                            { key: 'lettersIssued', label: 'Experience letter / Relieving letter issued.' }
                        ]}
                    />
                </div>
            </div>

            {/* --- PRINTABLE FORM VIEW --- */}
            <PrintableClearanceReport user={user} clearance={clearance} />
        </div>
    );
}

// =========================================================
// REUSABLE ACCORDION (Manager, HR, Finance)
// =========================================================
function ClearanceAccordion({ title, icon, data, canEdit, deptKey, isExpanded, onToggle, onSignOff, onUndo, checklistItems, blockingDepts = [] }) {
    const isCleared = data.status === 'Cleared';
    
    const [checks, setChecks] = useState(() => {
        const initialState = {};
        checklistItems.forEach(item => {
            initialState[item.key] = data[item.key] || false;
        });
        return initialState;
    });
    
    const [notes, setNotes] = useState(data.notes || '');
    
    const allChecked = checklistItems.every(item => checks[item.key]);
    const isBlocked = blockingDepts.length > 0;
    const canSignOff = allChecked && !isBlocked;

    return (
        <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${isCleared ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div onClick={onToggle} className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition">
                <div className="flex items-center space-x-3">
                    {icon}
                    <h3 className="font-bold text-lg text-[#213368]">{title}</h3>
                </div>
                <div className="flex items-center space-x-4">
                    {isCleared ? (
                        <span className="flex items-center text-xs font-bold text-green-700 bg-green-200 px-3 py-1 rounded-full">
                            <CheckCircleIcon className="w-4 h-4 mr-1" /> Cleared
                        </span>
                    ) : (
                        <span className="text-xs font-bold text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">Pending</span>
                    )}
                    <div className="text-gray-400">
                        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="p-6 border-t border-gray-100 bg-white">
                    <div className="space-y-4 mb-4">
                        {checklistItems.map(item => (
                            <label key={item.key} className={`flex items-start space-x-3 text-sm ${isCleared ? 'text-green-800' : 'text-gray-700'}`}>
                                <input 
                                    type="checkbox" 
                                    className="mt-1 w-4 h-4 text-[#20BEC6] rounded focus:ring-[#20BEC6]"
                                    checked={isCleared ? true : checks[item.key] || false}
                                    onChange={(e) => setChecks({...checks, [item.key]: e.target.checked})}
                                    disabled={isCleared || !canEdit}
                                />
                                <span className="leading-snug">{item.label}</span>
                            </label>
                        ))}
                    </div>

                    {!isCleared && (
                        <div className="mt-4 mb-4">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Notes / Comments (Optional)</label>
                            <textarea 
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                disabled={!canEdit}
                                placeholder="Add any relevant notes here..."
                                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] focus:border-[#20BEC6] outline-none resize-y"
                                rows="2"
                            />
                        </div>
                    )}

                    {/* --- NEW: Blocking Warning Box --- */}
                    {!isCleared && isBlocked && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm flex items-start">
                            <ExclamationIcon className="w-5 h-5 mr-2 flex-shrink-0" /> 
                            <p className="leading-snug">
                                <strong>Sign-off Blocked.</strong> The following departments must complete their clearance before HR can finalize this exit: 
                                <span className="font-bold ml-1">{blockingDepts.join(', ')}</span>.
                            </p>
                        </div>
                    )}

                    {isCleared && data.notes && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Notes</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.notes}</p>
                        </div>
                    )}

                    {isCleared ? (
                        <div className="text-xs text-green-700 mt-4 border-t border-green-100 pt-4 flex justify-between items-center">
                            <div className="flex items-center">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                <span>Signed off by <strong className="mx-1">{data.signedOffBy}</strong> on {new Date(data.signedOffAt).toLocaleDateString()}</span>
                            </div>
                            {canEdit && (
                                <button 
                                    onClick={() => onUndo(deptKey)} 
                                    className="text-[#D95448] hover:text-red-700 font-bold tracking-wide uppercase text-[10px] transition-colors"
                                >
                                    Undo Sign-off
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">
                            {!canEdit ? (
                                <span className="text-xs text-[#D95448] font-bold bg-red-50 px-3 py-1 rounded">
                                    You do not have permission to sign this off.
                                </span>
                            ) : (
                                <div className="flex justify-end w-full">
                                    <button 
                                        onClick={() => onSignOff(deptKey, { ...checks, notes })}
                                        disabled={!canSignOff}
                                        className="flex items-center text-[#20BEC6] hover:text-[#189b9f] disabled:text-gray-300 disabled:cursor-not-allowed transition"
                                        title={!canSignOff ? "Complete dependencies and checklist to sign off" : "Confirm Sign Off"}
                                    >
                                        <span className="text-sm font-bold mr-2 uppercase tracking-wide">Confirm & Sign Off</span>
                                        <CheckCircleIcon className="w-8 h-8" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// =========================================================
// CUSTOM IT ACCORDION (With Asset Fetching Logic)
// =========================================================
function ITClearanceAccordion({ appId, userId, data, canEdit, isExpanded, onToggle, onSignOff, onUndo }) {
    const isCleared = data.status === 'Cleared';
    
    const [checks, setChecks] = useState({
        idCard: data.idCard || false,
        laptopHandover: data.laptopHandover || false,
	emailBackup: data.emailBackup || false
    });
    const [notes, setNotes] = useState(data.notes || '');

    const [assignedAssets, setAssignedAssets] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(true);

    useEffect(() => {
        if (!isExpanded) return;
        const fetchAssets = async () => {
            const q = query(
                collection(db, `artifacts/${appId}/it_assets`),
                where('assignedToUserId', '==', userId),
                where('status', 'in', ['Active', 'Assigned'])
            );
            const snap = await getDocs(q);
            setAssignedAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoadingAssets(false);
        };
        fetchAssets();
    }, [appId, userId, isExpanded]);

    const hasAssetsRemaining = assignedAssets.length > 0;
    const allChecked = checks.idCard && checks.laptopHandover && checks.emailBackup;
    const canSignOff = allChecked && !hasAssetsRemaining;

    return (
        <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${isCleared ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div onClick={onToggle} className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition">
                <div className="flex items-center space-x-3">
                    <ComputerDesktopIcon className="w-6 h-6 text-[#213368]" /> 
                    <h3 className="font-bold text-lg text-[#213368]">IT</h3>
                </div>
                <div className="flex items-center space-x-4">
                    {isCleared ? (
                        <span className="flex items-center text-xs font-bold text-green-700 bg-green-200 px-3 py-1 rounded-full"><CheckCircleIcon className="w-4 h-4 mr-1" /> Cleared</span>
                    ) : (
                        <span className="text-xs font-bold text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">Pending</span>
                    )}
                    <div className="text-gray-400">
                        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="p-6 border-t border-gray-100 bg-white">
                    <div className="space-y-4 mb-4">
                        <label className={`flex items-start space-x-3 text-sm ${isCleared ? 'text-green-800' : 'text-gray-700'}`}>
                            <input type="checkbox" checked={isCleared ? true : checks.idCard || false} onChange={(e) => setChecks({...checks, idCard: e.target.checked})} disabled={isCleared || !canEdit} className="mt-1 w-4 h-4 text-[#20BEC6] rounded focus:ring-[#20BEC6]" />
                            <span className="leading-snug">ID card collected.</span>
                        </label>
                        <label className={`flex items-start space-x-3 text-sm ${isCleared ? 'text-green-800' : 'text-gray-700'}`}>
                            <input type="checkbox" checked={isCleared ? true : checks.laptopHandover || false} onChange={(e) => setChecks({...checks, laptopHandover: e.target.checked})} disabled={isCleared || !canEdit} className="mt-1 w-4 h-4 text-[#20BEC6] rounded focus:ring-[#20BEC6]" />
                            <span className="leading-snug">Asset handover completed (including charger, bags, and accessories).</span>
                        </label>
			<label className={`flex items-start space-x-3 text-sm ${isCleared ? 'text-green-800' : 'text-gray-700'}`}>
                            <input type="checkbox" checked={isCleared ? true : checks.emailBackup || false} onChange={(e) => setChecks({...checks, emailBackup: e.target.checked})} disabled={isCleared || !canEdit} className="mt-1 w-4 h-4 text-[#20BEC6] rounded focus:ring-[#20BEC6]" />
                            <span className="leading-snug">File transfer and email back up / google takeout process completed.</span>
                        </label>
                    </div>

                    {!isCleared && (
                        <div className="mt-4 mb-6">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Notes / Comments (Optional)</label>
                            <textarea 
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                disabled={!canEdit}
                                placeholder="Add any relevant IT notes here..."
                                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] focus:border-[#20BEC6] outline-none resize-y"
                                rows="2"
                            />
                        </div>
                    )}

                    {isCleared && data.notes && (
                        <div className="mt-4 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Notes</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{data.notes}</p>
                        </div>
                    )}

                    {!isCleared && (
                        <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                                <h4 className="text-xs font-bold text-[#213368] uppercase tracking-wide">Assigned Assets & Licenses</h4>
                                <span className="text-[10px] font-bold bg-white border border-gray-200 px-2 py-1 rounded-full text-gray-500">
                                    {assignedAssets.length} Items Found
                                </span>
                            </div>
                            
                            <div className="p-4">
                                {loadingAssets ? (
                                    <div className="text-xs text-gray-400 flex items-center"><LoadingSpinner /> <span className="ml-2">Scanning Inventory...</span></div>
                                ) : hasAssetsRemaining ? (
                                    <div className="space-y-3">
                                        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm flex items-start">
                                            <ExclamationIcon className="w-5 h-5 mr-2 flex-shrink-0" /> 
                                            <p className="leading-snug">
                                                <strong>Sign-off Blocked.</strong> The following assets must be unassigned from this user in the <a href="/it-inventory" className="underline font-bold">IT Inventory</a> module before you can proceed.
                                            </p>
                                        </div>
                                        <ul className="divide-y divide-gray-100 border border-gray-100 rounded bg-white">
                                            {assignedAssets.map(a => (
                                                <li key={a.id} className="p-3 hover:bg-gray-50 transition">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#20BEC6] border border-[#20BEC6] px-1.5 py-0.5 rounded mr-2">
                                                                {a.category}
                                                            </span>
                                                            <span className="text-sm font-bold text-gray-800">{a.assetTag}</span>
                                                        </div>
                                                        {a.serialNumber && <span className="text-[10px] text-gray-400 font-mono bg-gray-100 px-1 rounded">SN: {a.serialNumber}</span>}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {a.category === 'License / SaaS' 
                                                            ? `Plan: ${a.username || 'N/A'} | URL: ${a.url || 'N/A'}` 
                                                            : `${a.vendor} ${a.model}`}
                                                    </p>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="bg-green-50 border border-green-200 rounded p-3 text-green-800 text-sm font-bold flex items-center">
                                        <CheckCircleIcon className="w-5 h-5 mr-2" /> All system assets have been unassigned.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isCleared ? (
                        <div className="text-xs text-green-700 mt-4 border-t border-green-100 pt-4 flex justify-between items-center">
                            <div className="flex items-center">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                <span>Signed off by <strong className="mx-1">{data.signedOffBy}</strong> on {new Date(data.signedOffAt).toLocaleDateString()}</span>
                            </div>
                            {canEdit && (
                                <button 
                                    onClick={() => onUndo('it')} 
                                    className="text-[#D95448] hover:text-red-700 font-bold tracking-wide uppercase text-[10px] transition-colors"
                                >
                                    Undo Sign-off
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">
                            {!canEdit ? (
                                <span className="text-xs text-[#D95448] font-bold bg-red-50 px-3 py-1 rounded">
                                    You do not have permission to sign this off.
                                </span>
                            ) : (
                                <div className="flex justify-end w-full">
                                    <button 
                                        onClick={() => onSignOff('it', { ...checks, notes })}
                                        disabled={!canSignOff}
                                        className="flex items-center text-[#20BEC6] hover:text-[#189b9f] disabled:text-gray-300 disabled:cursor-not-allowed transition"
                                        title={!canSignOff ? "Clear all assets and checkboxes to sign off" : "Confirm Sign Off"}
                                    >
                                        <span className="text-sm font-bold mr-2 uppercase tracking-wide">Confirm & Sign Off</span>
                                        <CheckCircleIcon className="w-8 h-8" />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// =========================================================
// HIGHLY FORMAL PRINTABLE PDF VIEW
// =========================================================
function PrintableClearanceReport({ user, clearance }) {
    
    const PrintSection = ({ title, data, checks }) => (
        <div className="mb-6 border border-gray-400">
            <div className="bg-gray-100 border-b border-gray-400 p-2">
                <h3 className="text-md font-bold text-black uppercase tracking-wider">{title}</h3>
            </div>
            <div className="p-4">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <span className="font-bold text-gray-700 mr-2">Clearance Status:</span>
                        <span className={data.status === 'Cleared' ? 'font-bold text-black' : 'text-gray-500'}>{data.status}</span>
                    </div>
                    {data.status === 'Cleared' && (
                        <div className="text-sm text-right text-black">
                            <p><strong>Signed off by:</strong> {data.signedOffBy}</p>
                            <p><strong>Date:</strong> {new Date(data.signedOffAt).toLocaleDateString()}</p>
                        </div>
                    )}
                </div>
                
                <div className="space-y-2 mb-4">
                    {checks.map((check, i) => (
                        <div key={i} className="flex items-start text-sm">
                            <div className="w-5 h-5 border border-gray-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 text-black font-bold">
                                {data.status === 'Cleared' || data[check.key] ? '✓' : ''}
                            </div>
                            <span className="text-black">{check.label}</span>
                        </div>
                    ))}
                </div>

                {data.notes && (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-300 text-sm text-black">
                        <strong>Notes:</strong> {data.notes}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="hidden print:block bg-white text-black font-sans w-full max-w-4xl mx-auto">
            
            {/* Header */}
            <div className="flex justify-between items-center border-b-2 border-black pb-4 mb-6">
                <img src="/Brand_logo.png" alt="Company Logo" className="h-10 object-contain" />
                <div className="text-right">
                    <h1 className="text-2xl font-black uppercase tracking-widest text-black">Employee Exit Clearance Form</h1>
                    <p className="text-sm text-gray-600 mt-1">Generated: {new Date().toLocaleDateString()}</p>
                </div>
            </div>

            {/* Employee Details Grid */}
            <div className="border-2 border-black mb-8">
                <div className="bg-gray-100 border-b-2 border-black p-2">
                    <h2 className="text-md font-bold uppercase tracking-wider text-black">Employee Details</h2>
                </div>
                <div className="grid grid-cols-2 divide-x-2 divide-black text-black">
                    <div className="p-3 border-b-2 border-black">
                        <p className="text-xs text-gray-500 uppercase font-bold">Name</p>
                        <p className="text-base font-bold text-black">{user.name}</p>
                    </div>
                    <div className="p-3 border-b-2 border-black">
                        <p className="text-xs text-gray-500 uppercase font-bold">Employee ID</p>
                        <p className="text-base font-bold text-black">{user.employeeId || 'N/A'}</p>
                    </div>
                    <div className="p-3 border-b-2 border-black">
                        <p className="text-xs text-gray-500 uppercase font-bold">Designation</p>
                        <p className="text-base font-bold text-black">{user.designation || 'N/A'}</p>
                    </div>
                    <div className="p-3 border-b-2 border-black">
                        <p className="text-xs text-gray-500 uppercase font-bold">Department</p>
                        <p className="text-base font-bold text-black">{user.department || 'N/A'}</p>
                    </div>
                    <div className="p-3">
                        <p className="text-xs text-gray-500 uppercase font-bold">Last Working Date</p>
                        <p className="text-base font-bold text-black">{new Date(user.lastWorkingDate.toDate()).toLocaleDateString()}</p>
                    </div>
                    <div className="p-3">
                        <p className="text-xs text-gray-500 uppercase font-bold">Overall Clearance Status</p>
                        <p className="text-base font-bold text-black">{clearance.overallStatus}</p>
                    </div>
                </div>
            </div>

            {/* Form Sections */}
            <PrintSection 
                title="1. Reporting Manager Clearance" 
                data={clearance.manager} 
                checks={[{ key: 'ipHandover', label: 'Handover & transition of all work-related data and IP completed.' }]} 
            />
            
            <PrintSection 
                title="2. IT Department Clearance" 
                data={clearance.it} 
                checks={[
                    { key: 'idCard', label: 'ID card collected.' },
                    { key: 'laptopHandover', label: 'Asset handover completed (including charger, bags, and accessories).' },
		    { key: 'emailBackup', label: 'File transfer and email back up / google takeout process completed.' }
                ]} 
            />

            <PrintSection 
                title="3. Accounts & Finance Clearance" 
                data={clearance.finance} 
                checks={[{ key: 'fnfCleared', label: 'Full and Final (F&F) Settlement cleared. No outstanding dues or loans.' }]} 
            />

            <PrintSection 
                title="4. HR Clearance" 
                data={clearance.hr} 
                checks={[
                    { key: 'idCardDeactivated', label: 'ID card deactivated.' },
                    { key: 'accessRevoked', label: 'Biometric / other access revoked.' },
                    { key: 'deskCleared', label: 'Desk and drawers checked / cleared.' },
                    { key: 'lettersIssued', label: 'Experience letter / Relieving letter issued.' }
                ]} 
            />

            <div className="mt-8 pt-4 text-center text-xs text-gray-500 border-t border-gray-300">
                <p>This is a system-generated document. Digital sign-offs are securely logged in the database.</p>
            </div>
        </div>
    );
}